import { CircularProgress } from '@mui/material';
import { FC, useEffect } from 'react';
import { DetailTable } from './detailView/DetailTable';
import { DocumentType, graphql } from '../../gql';
import { useApiQuery } from '../../query/hooks';
import { z } from 'zod';
import { useTypedPathParams } from '../../../../shared/fe/hooks/useTypedPathParams';
import { Box } from '@mui/system';
import { Outlet } from 'react-router-dom';
import { useJourneyFilters } from '../useJourneyFilters';
import { applyJourneyFilters } from '../applyJourneyFilters';
import { useGeneratePDF } from '../../hooks/useGeneratePDF';

const journeyDetail_getJourneyDetail = graphql(`
	query journeyDetail_getJourneyDetail($id: UUID!) {
		getJourney(by: { id: $id }) {
			id
			name
			widgets {
				id
				order
				name
				type
			}
			phases {
				id
				name
				description
				contentMetrics {
					id
				}
				touchpoints(filter: { type: { eq: activity } }) {
					id
					activity {
						id
						name
						description
						warnings {
							id
						}
						...journeyDetail_widgets
					}
				}
				...journeyDetail_phases
				...journeyFilters_phases
			}
		}
	}
`);

export type Journey = DocumentType<typeof journeyDetail_getJourneyDetail>['getJourney'];
export type Phase = NonNullable<Journey>['phases'][number];

export const journeyDetailSchema = z.object({
	id: z.string(),
});
export const detailSubactivtySchema = z.object({
	subactivityId: z.string().optional(),
});

const JourneyDetailPage: FC = () => {
	const { id } = useTypedPathParams(journeyDetailSchema);
	const { subactivityId } = useTypedPathParams(detailSubactivtySchema);
	const { data: journey, isLoading } = useApiQuery(journeyDetail_getJourneyDetail, { variables: { id: id } });
	const [filters] = useJourneyFilters();
	const phasesFiltered = applyJourneyFilters({ phases: journey?.getJourney?.phases ?? [], filters });
	const { setPdfExportData } = useGeneratePDF();

	useEffect(() => {
		setPdfExportData({
			elementId: 'detail-table',
			fileName: `${journey?.getJourney?.name}_detail` || 'export',
			orientation: 'l',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [journey?.getJourney?.name]);

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
				<CircularProgress />
			</Box>
		);
	}

	if (subactivityId != null) {
		return <Outlet />;
	}

	return journey?.getJourney != null && <DetailTable id={id} phases={phasesFiltered} widgets={journey.getJourney.widgets} />;
};

export default JourneyDetailPage;
